import { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import { useProjectStore } from "../../../stores/projectStore";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import { useWebSocket } from "../../SocketHost/WebSocketProvider";
import JobProgressBanner from "../JobProgressBanner";

const TERMINAL_STATES = new Set(["completed", "partial", "failed"]);

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .row { display: flex; align-items: center; gap: 10px; }
  .title {
    color: var(--grey-1);
    cursor: pointer;
    flex: 1;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .show-me { align-self: flex-start; }
  .failure-list { display: flex; flex-direction: column; gap: 8px; max-height: 180px; overflow-y: auto; }
`;

export default function ProjectSpacingMigrationProgressBanner({ projectId, onNavigateDocument }) {
  const project = useProjectStore((state) => state.project);
  const pendingCleanup = useProjectStore((state) => state.pendingFrontendSpacingCleanup);
  const resolvedIds = useProjectStore((state) => state.locallyResolvedSpacingCleanupDocumentIds);
  const resetRetryState = useProjectStore((state) => state.resetSpacingCleanupRetryState);
  const setPendingCleanup = useProjectStore((state) => state.setPendingFrontendSpacingCleanup);
  const { registerHandler, unregisterHandler } = useWebSocket();
  const savedJob = project?.id === projectId ? project.spacingCleanupJob : null;
  const jobId = savedJob?.jobId || null;
  const [liveStatus, setLiveStatus] = useState(null);
  const [isBulkRetrying, setIsBulkRetrying] = useState(false);
  const [showFailedDocuments, setShowFailedDocuments] = useState(false);

  useEffect(() => {
    setLiveStatus(null);
    setShowFailedDocuments(false);
    resetRetryState(jobId);
  }, [jobId, resetRetryState]);

  useEffect(() => {
    if (!jobId || savedJob.state !== "running") return undefined;
    let cancelled = false;

    const loadStatus = async () => {
      try {
        const status = await project.getSpacingCleanupStatus(jobId);
        if (cancelled) return;
        setLiveStatus(status);
      } catch {
        // Live websocket updates can still populate the card if this request fails.
      }
    };

    void loadStatus();
    return () => {
      cancelled = true;
    };
  }, [jobId, project, savedJob?.state]);

  useEffect(() => {
    if (!jobId) return undefined;
    const handler = (message) => {
      const payload = message?.payload;
      if (payload?.projectId === projectId && payload?.jobId === jobId) {
        setLiveStatus(payload);
      }
    };
    registerHandler("projectSpacingMigrationProgress", handler);
    return () => unregisterHandler("projectSpacingMigrationProgress");
  }, [jobId, projectId, registerHandler, unregisterHandler]);

  const status = TERMINAL_STATES.has(savedJob?.state) ? savedJob : liveStatus || savedJob;
  const progress = status?.progress || status;
  const failedDocumentIds = status?.failedDocumentIds || progress?.failedDocumentIds || [];
  const resolvedSet = useMemo(() => new Set(resolvedIds), [resolvedIds]);
  const unresolvedFailures = failedDocumentIds.filter((id) => !resolvedSet.has(id));
  const locallyComplete = status?.state === "partial" && failedDocumentIds.length > 0 && unresolvedFailures.length === 0;
  const terminal = TERMINAL_STATES.has(status?.state) || locallyComplete;
  const successful = status?.state === "completed" || locallyComplete;

  useEffect(() => {
    if (!jobId || !successful) return undefined;
    const timeoutId = window.setTimeout(async () => {
      await project.dismissSpacingCleanup(jobId);
      resetRetryState(null);
    }, 10_000);
    return () => window.clearTimeout(timeoutId);
  }, [jobId, project, resetRetryState, successful]);

  if (!jobId) return null;

  const total = progress?.total ?? savedJob.total ?? 0;
  const completed = Math.min(total, (progress?.completed || 0) + (failedDocumentIds.length - unresolvedFailures.length));
  const ratio = total === 0 ? 1 : completed / total;
  const titleById = new Map((project.contents || []).map((item) => [item.id, item.title || "Untitled"]));

  let statusText = `${completed} of ${total} documents complete`;
  if (status?.state === "running" && completed === 0) statusText = "Starting project cleanup…";
  if (status?.state === "failed") statusText = "Project cleanup could not be completed.";
  if (status?.state === "completed" || locallyComplete) statusText = `${total} of ${total} documents complete`;

  const dismiss = terminal
    ? async () => {
        await project.dismissSpacingCleanup(jobId);
        resetRetryState(null);
      }
    : undefined;

  const bulkRetry = async () => {
    setIsBulkRetrying(true);
    try {
      await project.startSpacingCleanup({ documentIds: unresolvedFailures });
    } finally {
      setIsBulkRetrying(false);
    }
  };

  const failureRow = (documentId) => {
    const opening = pendingCleanup?.jobId === jobId && pendingCleanup?.documentId === documentId;
    const documentTitle = titleById.get(documentId) || "Untitled";
    const navigateToDocument = () => onNavigateDocument?.(documentId);
    return (
      <div className="row" key={documentId}>
        <span
          role="button"
          tabIndex={0}
          className="title"
          title={documentTitle}
          onClick={navigateToDocument}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              navigateToDocument();
            }
          }}
        >
          {documentTitle}
        </span>
        <SudoButton
          type="button"
          variant="tertiaryGrey"
          size="small"
          disabled={opening}
          onClick={() => {
            setPendingCleanup({ projectId, jobId, documentId });
            onNavigateDocument?.(documentId);
          }}
        >
          {opening ? "Opening…" : "Retry"}
        </SudoButton>
      </div>
    );
  };

  return (
    <JobProgressBanner
      title="Project cleanup"
      statusText={statusText}
      progress={ratio}
      isComplete={terminal}
      onDismiss={dismiss}
      bottomOffset={project.importStep ? 164 : 16}
      footnote={status?.state === "partial" && unresolvedFailures.length > 0
        ? `We couldn't clean up ${unresolvedFailures.length} ${unresolvedFailures.length === 1 ? "document" : "documents"}.`
        : undefined}
    >
      {status?.state === "partial" && unresolvedFailures.length > 0 ? (
        <Actions>
          {unresolvedFailures.length <= 5 ? unresolvedFailures.map(failureRow) : (
            <>
              <SudoButton
                type="button"
                variant="primary"
                className="w-full"
                disabled={isBulkRetrying}
                onClick={() => void bulkRetry()}
              >
                {isBulkRetrying ? "Retrying…" : `Retry ${unresolvedFailures.length} failed documents`}
              </SudoButton>
              <SudoButton
                type="button"
                variant="tertiaryGrey"
                size="small"
                className="show-me"
                aria-expanded={showFailedDocuments}
                leftIcon={showFailedDocuments ? "/assets/chevron-down.svg" : "/assets/chevron-right.svg"}
                onClick={() => setShowFailedDocuments((showing) => !showing)}
              >
                Show me
              </SudoButton>
              {showFailedDocuments ? (
                <div className="failure-list" aria-label="Documents needing cleanup">
                  {unresolvedFailures.map(failureRow)}
                </div>
              ) : null}
            </>
          )}
        </Actions>
      ) : null}
    </JobProgressBanner>
  );
}
